import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';

import type { Point } from '../../shared/lib/pointer-drag';

import {
  isHorizontalIntent,
  panelOffset,
  shouldCommit,
  velocityOf,
  type DrawerGesture,
  type Sample,
} from './drawer-swipe';

export interface DrawerSwipeOptions {
  /** Only on the phone layout. */
  enabled: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  dialogRef: RefObject<HTMLDialogElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  reducedMotion: boolean;
}

export interface DrawerSwipe {
  /** Spread onto the invisible left-edge strip. */
  edgeProps: { onPointerDown: (event: React.PointerEvent<HTMLElement>) => void };
  /** Spread onto the drawer panel. */
  panelProps: { onPointerDown: (event: React.PointerEvent<HTMLElement>) => void };
  /** Slide the panel out, then close. For the X button and the backdrop. */
  requestClose: () => void;
}

interface Gesture {
  kind: DrawerGesture;
  pointerId: number;
  origin: Point;
  armed: boolean;
  width: number;
  dx: number;
  prev: Sample;
  last: Sample;
}

/** How long the settle transition may take before we stop waiting for it. */
const SETTLE_FALLBACK_MS = 260;
/** The drawer panel's width, mirrored from AppShell.module.css. */
const PANEL_MAX_PX = 300;
const PANEL_VW = 0.86;

/**
 * Swipe-from-edge to open the drawer, swipe-left on it to close.
 *
 * The panel follows the finger by a transform written straight to the node;
 * on release the gesture commits or springs back with a short transition, and
 * only then does React learn anything. The `<dialog>` stays modal throughout —
 * nothing here re-implements its focus trap, ESC or backdrop. Move/up
 * listeners live on `document` for the life of one gesture (the same pattern
 * as usePointerDrag), read by pointerId, which is what survives the dialog
 * going modal mid-gesture.
 *
 * Under reduced motion nothing is animated: the gesture still arms, and on
 * release the same commit rule decides open or close.
 *
 * Known limit: Safari-as-a-browser owns the left screen edge for its own
 * back-swipe. In standalone (installed) mode there is no system gesture and
 * this one works; the hamburger and the More tab cover both.
 */
export function useDrawerSwipe(options: DrawerSwipeOptions): DrawerSwipe {
  const latest = useRef(options);
  latest.current = options;
  const gesture = useRef<Gesture | null>(null);
  const settleTimer = useRef<number | null>(null);

  const panelWidth = useCallback((): number => {
    const rect = latest.current.panelRef.current?.getBoundingClientRect();
    return rect !== undefined && rect.width > 0
      ? rect.width
      : Math.min(PANEL_MAX_PX, window.innerWidth * PANEL_VW);
  }, []);

  const paint = useCallback((offset: number, width: number): void => {
    const panel = latest.current.panelRef.current;
    const dialog = latest.current.dialogRef.current;
    if (panel) panel.style.transform = `translateX(${offset}px)`;
    if (dialog) dialog.style.setProperty('--drawer-progress', String(1 + offset / width));
  }, []);

  const clearStyles = useCallback((): void => {
    const panel = latest.current.panelRef.current;
    const dialog = latest.current.dialogRef.current;
    if (panel) {
      panel.style.transform = '';
      panel.style.transition = '';
      delete panel.dataset['dragging'];
      delete panel.dataset['settling'];
    }
    if (dialog) dialog.style.removeProperty('--drawer-progress');
  }, []);

  /**
   * Transition the panel to `offset` (0 = open, -width = closed) then run
   * `then`. With reduced motion, or no panel, runs `then` at once.
   */
  const settle = useCallback(
    (offset: number, width: number, then: () => void): void => {
      const panel = latest.current.panelRef.current;
      if (latest.current.reducedMotion || panel === null) {
        then();
        return;
      }
      delete panel.dataset['dragging'];
      panel.dataset['settling'] = 'true';
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        panel.removeEventListener('transitionend', finish);
        if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
        settleTimer.current = null;
        then();
      };
      panel.addEventListener('transitionend', finish);
      settleTimer.current = window.setTimeout(finish, SETTLE_FALLBACK_MS);
      // Force a style flush so the transition runs from the current transform.
      void panel.getBoundingClientRect();
      paint(offset, width);
    },
    [paint],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent): void => {
      const g = gesture.current;
      if (g === null || event.pointerId !== g.pointerId) return;
      const point = { x: event.clientX, y: event.clientY };
      if (!g.armed) {
        if (!isHorizontalIntent(g.origin, point)) {
          // Not yet — or a vertical scroll, which is the page's business.
          const dy = Math.abs(point.y - g.origin.y);
          if (dy >= 8 && dy > Math.abs(point.x - g.origin.x)) release(false);
          return;
        }
        const dx = point.x - g.origin.x;
        if ((g.kind === 'open' && dx <= 0) || (g.kind === 'close' && dx >= 0)) {
          release(false);
          return;
        }
        g.armed = true;
        g.width = panelWidth();
        const panel = latest.current.panelRef.current;
        if (panel && !latest.current.reducedMotion) panel.dataset['dragging'] = 'true';
        if (g.kind === 'open') latest.current.onOpen();
      }
      if (event.cancelable) event.preventDefault();
      g.dx = point.x - g.origin.x;
      g.prev = g.last;
      g.last = { x: point.x, t: performance.now() };
      if (!latest.current.reducedMotion) paint(panelOffset(g.kind, g.dx, g.width), g.width);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `release` is defined below and stable; both close over refs only
    [paint, panelWidth],
  );

  const detach = useCallback((): void => {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerCancel);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the up/cancel handlers are hoisted function declarations below
  }, [onPointerMove]);

  function release(commit: boolean): void {
    const g = gesture.current;
    if (g === null) return;
    gesture.current = null;
    detach();
    if (!g.armed) return;
    const { kind, width } = g;
    if (kind === 'open') {
      if (commit) settle(0, width, clearStyles);
      else
        settle(-width, width, () => {
          clearStyles();
          latest.current.onClose();
        });
    } else if (commit) {
      settle(-width, width, () => {
        clearStyles();
        latest.current.onClose();
      });
    } else {
      settle(0, width, clearStyles);
    }
  }

  function onPointerUp(event: PointerEvent): void {
    const g = gesture.current;
    if (g === null || event.pointerId !== g.pointerId) return;
    const velocity = velocityOf(g.prev, g.last);
    release(g.armed && shouldCommit(g.kind, g.dx, velocity, g.width));
  }

  function onPointerCancel(event: PointerEvent): void {
    const g = gesture.current;
    if (g === null || event.pointerId !== g.pointerId) return;
    release(false);
  }

  const start = useCallback(
    (kind: DrawerGesture, event: React.PointerEvent<HTMLElement>): void => {
      const { enabled, open } = latest.current;
      if (!enabled || gesture.current !== null || event.button !== 0) return;
      if (kind === 'open' ? open : !open) return;
      const now = performance.now();
      gesture.current = {
        kind,
        pointerId: event.pointerId,
        origin: { x: event.clientX, y: event.clientY },
        armed: false,
        width: 0,
        dx: 0,
        prev: { x: event.clientX, t: now },
        last: { x: event.clientX, t: now },
      };
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerCancel);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the up/cancel handlers are hoisted function declarations above
    [onPointerMove],
  );

  const requestClose = useCallback((): void => {
    if (!latest.current.open || gesture.current !== null) return;
    settle(-panelWidth(), panelWidth(), () => {
      clearStyles();
      latest.current.onClose();
    });
  }, [settle, panelWidth, clearStyles]);

  // Whatever a gesture left on the panel is wiped once the drawer is closed,
  // so the next tap-open plays its ordinary entrance.
  useEffect(() => {
    if (!options.open) clearStyles();
  }, [options.open, clearStyles]);

  useEffect(
    () => () => {
      gesture.current = null;
      detach();
      if (settleTimer.current !== null) window.clearTimeout(settleTimer.current);
    },
    [detach],
  );

  return useMemo(
    () => ({
      edgeProps: {
        onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
          start('open', event);
        },
      },
      panelProps: {
        onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
          start('close', event);
        },
      },
      requestClose,
    }),
    [start, requestClose],
  );
}
